/**
 * Main entry point for the coding agent CLI.
 *
 * This file handles CLI argument parsing and translates them into
 * createAgentSession() options. The SDK does the heavy lifting.
 */

import { createInterface } from "node:readline";
import { assertValidSessionId, SessionManager } from "@caupulican/pi-agent-core/session";
import { type ImageContent, modelsAreEqual } from "@caupulican/pi-ai";
import { ProcessTerminal, setKeybindings, TUI } from "@caupulican/pi-tui";
import chalk from "chalk";
import { type Args, type Mode, parseArgs, printHelp } from "./cli/args.ts";
import { processFileArguments } from "./cli/file-processor.ts";
import { buildInitialMessage } from "./cli/initial-message.ts";
import { listModels } from "./cli/list-models.ts";
import { readPipedInput } from "./cli/piped-stdin.ts";
import { selectSession } from "./cli/session-picker.ts";
import { ENV_SESSION_DIR, expandTildePath, getAgentDir, getPackageDir, VERSION } from "./config.ts";
import {
	type AgentSessionRuntime,
	type CreateAgentSessionRuntimeFactory,
	createAgentSessionRuntime,
} from "./core/agent-session-runtime.ts";
import {
	type AgentSessionRuntimeDiagnostic,
	createAgentSessionFromServices,
	createAgentSessionServices,
} from "./core/agent-session-services.ts";
import { formatNoModelsAvailableMessage } from "./core/auth-guidance.ts";
import { AuthStorage } from "./core/auth-storage.ts";
import { formatDoctorReport, runDoctor, runUpdatePreflight } from "./core/doctor.ts";
import { exportFromFile } from "./core/export-html/index.ts";
import type { ExtensionFactory } from "./core/extensions/types.ts";
import { configureHttpDispatcher } from "./core/http-dispatcher.ts";
import { KeybindingsManager } from "./core/keybindings.ts";
import { formatLaneWorkerRefusal } from "./core/model-capability.ts";
import type { ModelRegistry } from "./core/model-registry.ts";
import {
	resolveCliModel,
	resolveCliProviderDefault,
	resolveModelScope,
	resolveProfileModelSettings,
	type ScopedModel,
} from "./core/model-resolver.ts";
import { collectModelRouterConfigDiagnostics } from "./core/model-router/config-diagnostics.ts";
import type { OrchestrationProfile } from "./core/orchestration/contracts.ts";
import { resolveConfiguredOrchestrationModel } from "./core/orchestration/model-binding.ts";
import { OrchestrationProfileStore } from "./core/orchestration/profile-store.ts";
import { restoreStdout, takeOverStdout } from "./core/output-guard.ts";
import type { ResumablePayload } from "./core/process-matrix/codes.ts";
import { launchResumablePiAgent } from "./core/process-matrix/resume-launcher.ts";
import {
	PI_PARENT_PID_ENV,
	PI_PARENT_SESSION_ENV,
	PI_TASK_REF_ENV,
	type ResumeWorkerLaunchOutcome,
} from "./core/process-matrix/runtime.ts";
import { isReloadSessionProcessAlive } from "./core/reload-blockers.ts";
import { parseResourceProfileInput } from "./core/resource-profile-blocks.ts";
import type { CreateAgentSessionOptions } from "./core/sdk.ts";
import {
	formatMissingSessionCwdPrompt,
	getMissingSessionCwdIssue,
	MissingSessionCwdError,
	type SessionCwdIssue,
} from "./core/session-cwd.ts";
import {
	continueRecentSession,
	createSession,
	forkSession,
	listAllSessions,
	listSessions,
	openSession,
} from "./core/session-manager-factory.ts";
import { getSessionRole, setTerminalSessionMode } from "./core/session-role.ts";
import { SessionSupervisionRuntime } from "./core/session-supervision-runtime.ts";
import { SettingsManager } from "./core/settings-manager.ts";
import { printTimings, resetTimings, time } from "./core/timings.ts";
import { hasProjectTrustInputs, ProjectTrustStore } from "./core/trust-manager.ts";
import { getBoundWorktreeLaneKey, PI_WORKTREE_LANE_ENV } from "./core/worktree-sync/runtime.ts";
import { runMigrations, showDeprecationWarnings } from "./migrations.ts";

import { handleConfigCommand, handlePackageCommand } from "./package-manager-cli.ts";
import { isLocalPath, normalizePath, resolvePath } from "./utils/paths.ts";
import { cleanupWindowsSelfUpdateQuarantine } from "./utils/windows-self-update.ts";
import { getProcessWorkRun, getWorkRoot, PI_WORK_ROOT_ENV } from "./utils/work-directory.ts";

function getSelfLaunchTarget(): { executable: string; argsPrefix: string[] } | undefined {
	const executableName = process.execPath.split(/[\\/]/).at(-1)?.toLowerCase();
	const isScriptRuntime = ["node", "node.exe", "bun", "bun.exe"].includes(executableName ?? "");
	if (!isScriptRuntime) return { executable: process.execPath, argsPrefix: [] };
	const cliPath = process.argv[1];
	return cliPath && !cliPath.startsWith("-") ? { executable: process.execPath, argsPrefix: [cliPath] } : undefined;
}

async function launchResumableWorker(
	payload: ResumablePayload,
	parentSessionId: string,
): Promise<ResumeWorkerLaunchOutcome> {
	const target = getSelfLaunchTarget();
	if (!target) return { started: false, reason: "The current Pi executable cannot be resolved." };
	return launchResumablePiAgent({
		payload,
		target,
		parentPid: process.pid,
		parentSessionId,
		environment: process.env,
	});
}

function collectSettingsDiagnostics(
	settingsManager: SettingsManager,
	context: string,
): AgentSessionRuntimeDiagnostic[] {
	return settingsManager.drainErrors().map(({ scope, error }) => ({
		type: "warning",
		message: `(${context}, ${scope} settings) ${error.message}`,
	}));
}

function reportDiagnostics(diagnostics: readonly AgentSessionRuntimeDiagnostic[]): void {
	for (const diagnostic of diagnostics) {
		const color = diagnostic.type === "error" ? chalk.red : diagnostic.type === "warning" ? chalk.yellow : chalk.dim;
		const prefix = diagnostic.type === "error" ? "Error: " : diagnostic.type === "warning" ? "Warning: " : "";
		console.error(color(`${prefix}${diagnostic.message}`));
	}
}

export async function disposeRuntimeAndExit(
	runtime: Pick<AgentSessionRuntime, "dispose">,
	exitCode: number,
	exit: (code: number) => void = (code) => process.exit(code),
): Promise<void> {
	try {
		await runtime.dispose();
	} finally {
		exit(exitCode);
	}
}

function isTruthyEnvFlag(value: string | undefined): boolean {
	if (!value) return false;
	return value === "1" || value.toLowerCase() === "true" || value.toLowerCase() === "yes";
}

type AppMode = "interactive" | "print" | "json" | "rpc";

function resolveAppMode(parsed: Args, stdinIsTTY: boolean): AppMode {
	if (parsed.mode === "rpc") {
		return "rpc";
	}
	if (parsed.mode === "json") {
		return "json";
	}
	if (parsed.print || !stdinIsTTY) {
		return "print";
	}
	return "interactive";
}

function toPrintOutputMode(appMode: AppMode): Exclude<Mode, "rpc"> {
	return appMode === "json" ? "json" : "text";
}

async function prepareInitialMessage(
	parsed: Args,
	autoResizeImages: boolean,
	stdinContent?: string,
): Promise<{
	initialMessage?: string;
	initialImages?: ImageContent[];
}> {
	if (parsed.fileArgs.length === 0) {
		return buildInitialMessage({ parsed, stdinContent });
	}

	const { text, images } = await processFileArguments(parsed.fileArgs, { autoResizeImages });
	return buildInitialMessage({
		parsed,
		fileText: text,
		fileImages: images,
		stdinContent,
	});
}

/** Result from resolving a session argument */
type ResolvedSession =
	| { type: "path"; path: string } // Direct file path
	| { type: "local"; path: string } // Found in current project
	| { type: "global"; path: string; cwd: string } // Found in different project
	| { type: "not_found"; arg: string }; // Not found anywhere

/**
 * Resolve a session argument to a file path.
 * If it looks like a path, use as-is. Otherwise try to match as session ID prefix.
 */
async function findLocalSessionByExactId(
	sessionId: string,
	cwd: string,
	sessionDir?: string,
): Promise<{ type: "local"; path: string } | undefined> {
	const localSessions = await listSessions(cwd, sessionDir);
	const localMatch = localSessions.find((s) => s.id === sessionId);
	return localMatch ? { type: "local", path: localMatch.path } : undefined;
}

async function resolveSessionPath(sessionArg: string, cwd: string, sessionDir?: string): Promise<ResolvedSession> {
	// If it looks like a file path, resolve it before handing it to the session manager.
	if (sessionArg.includes("/") || sessionArg.includes("\\") || sessionArg.endsWith(".jsonl")) {
		return { type: "path", path: resolvePath(sessionArg, cwd) };
	}

	// Try to match as session ID in current project first
	const localSessions = await listSessions(cwd, sessionDir);
	const localMatch =
		localSessions.find((s) => s.id === sessionArg) ?? localSessions.find((s) => s.id.startsWith(sessionArg));

	if (localMatch) {
		return { type: "local", path: localMatch.path };
	}

	// Try global search across all projects
	const allSessions = await listAllSessions(sessionDir);
	const globalMatch =
		allSessions.find((s) => s.id === sessionArg) ?? allSessions.find((s) => s.id.startsWith(sessionArg));

	if (globalMatch) {
		return { type: "global", path: globalMatch.path, cwd: globalMatch.cwd };
	}

	// Not found anywhere
	return { type: "not_found", arg: sessionArg };
}

/** Prompt user for yes/no confirmation. Non-interactive callers fail closed instead of waiting on stdin. */
export async function promptConfirm(message: string): Promise<boolean> {
	if (!process.stdin.isTTY || !process.stdout.isTTY) return false;
	return new Promise((resolve) => {
		const rl = createInterface({
			input: process.stdin,
			output: process.stdout,
		});
		let settled = false;
		const finish = (confirmed: boolean) => {
			if (settled) return;
			settled = true;
			rl.removeListener("close", onClose);
			rl.close();
			resolve(confirmed);
		};
		const onClose = () => finish(false);
		rl.once("close", onClose);
		rl.question(`${message} [y/N] `, (answer) => {
			const normalized = answer.trim().toLowerCase();
			finish(normalized === "y" || normalized === "yes");
		});
	});
}

function collectSessionModeConflicts(parsed: Args): string[] {
	return [
		parsed.session ? "--session" : undefined,
		parsed.continue ? "--continue" : undefined,
		parsed.resume ? "--resume" : undefined,
		parsed.noSession ? "--no-session" : undefined,
	].filter((flag): flag is string => flag !== undefined);
}

function validateForkFlags(parsed: Args): void {
	if (!parsed.fork) return;

	const conflictingFlags = collectSessionModeConflicts(parsed);
	if (conflictingFlags.length > 0) {
		console.error(chalk.red(`Error: --fork cannot be combined with ${conflictingFlags.join(", ")}`));
		process.exit(1);
	}
}

function validateSessionIdFlags(parsed: Args): void {
	if (parsed.sessionId === undefined) return;

	const conflictingFlags = collectSessionModeConflicts(parsed);
	if (conflictingFlags.length > 0) {
		console.error(chalk.red(`Error: --session-id cannot be combined with ${conflictingFlags.join(", ")}`));
		process.exit(1);
	}

	try {
		assertValidSessionId(parsed.sessionId);
	} catch (error: unknown) {
		const message = error instanceof Error ? error.message : String(error);
		console.error(chalk.red(`Error: ${message}`));
		process.exit(1);
	}
}

function forkSessionOrExit(sourcePath: string, cwd: string, sessionDir?: string, sessionId?: string): SessionManager {
	try {
		return forkSession(sourcePath, cwd, sessionDir, { id: sessionId });
	} catch (error: unknown) {
		const message = error instanceof Error ? error.message : String(error);
		console.error(chalk.red(`Error: ${message}`));
		process.exit(1);
	}
}

async function createSessionManager(
	parsed: Args,
	cwd: string,
	sessionDir: string | undefined,
	settingsManager: SettingsManager,
	hasHumanUI: boolean,
): Promise<SessionManager> {
	if (parsed.noSession || parsed.help || parsed.listModels !== undefined) {
		return SessionManager.inMemory(cwd);
	}

	if (parsed.fork) {
		if (parsed.sessionId) {
			const existingTarget = await findLocalSessionByExactId(parsed.sessionId, cwd, sessionDir);
			if (existingTarget) {
				console.error(chalk.red(`Session already exists with id '${parsed.sessionId}'`));
				process.exit(1);
			}
		}

		const resolved = await resolveSessionPath(parsed.fork, cwd, sessionDir);

		switch (resolved.type) {
			case "path":
			case "local":
			case "global":
				return forkSessionOrExit(resolved.path, cwd, sessionDir, parsed.sessionId);

			case "not_found":
				console.error(chalk.red(`No session found matching '${resolved.arg}'`));
				process.exit(1);
		}
	}

	if (parsed.session) {
		const resolved = await resolveSessionPath(parsed.session, cwd, sessionDir);

		switch (resolved.type) {
			case "path":
			case "local":
				return openSession(resolved.path, sessionDir);

			case "global": {
				if (!hasHumanUI) {
					console.error(chalk.red("Cannot open a cross-project session without a user approval UI."));
					process.exit(1);
				}
				console.log(chalk.yellow(`Session found in different project: ${resolved.cwd}`));
				const shouldFork = await promptConfirm("Fork this session into current directory?");
				if (!shouldFork) {
					console.log(chalk.dim("Aborted."));
					process.exit(0);
				}
				return forkSessionOrExit(resolved.path, cwd, sessionDir);
			}

			case "not_found":
				console.error(chalk.red(`No session found matching '${resolved.arg}'`));
				process.exit(1);
		}
	}

	if (parsed.resume) {
		if (!hasHumanUI) {
			console.error(chalk.red("Error: --resume requires --session-mode user; use --session <id> for workers."));
			process.exit(1);
		}
		const { initTheme, stopThemeWatcher } = await import("./modes/interactive/theme/theme.ts");
		initTheme(settingsManager.getTheme(), true);
		try {
			const selectedPath = await selectSession(
				(onProgress) => listSessions(cwd, sessionDir, onProgress),
				(onProgress) => listAllSessions(sessionDir, onProgress),
			);
			if (!selectedPath) {
				console.log(chalk.dim("No session selected"));
				process.exit(0);
			}
			return openSession(selectedPath, sessionDir);
		} finally {
			stopThemeWatcher();
		}
	}

	if (parsed.continue) {
		return continueRecentSession(cwd, sessionDir);
	}

	if (parsed.sessionId) {
		const existingSession = await findLocalSessionByExactId(parsed.sessionId, cwd, sessionDir);
		if (existingSession) {
			return openSession(existingSession.path, sessionDir);
		}
	}

	return createSession(cwd, sessionDir, { id: parsed.sessionId });
}

function buildSessionOptions(
	parsed: Args,
	scopedModels: ScopedModel[],
	hasExistingSession: boolean,
	modelRegistry: ModelRegistry,
	settingsManager: SettingsManager,
	cwd: string,
): {
	options: CreateAgentSessionOptions;
	cliThinkingFromModel: boolean;
	diagnostics: AgentSessionRuntimeDiagnostic[];
} {
	const options: CreateAgentSessionOptions = {};
	const diagnostics: AgentSessionRuntimeDiagnostic[] = [];
	let cliThinkingFromModel = false;

	// Model from CLI
	// - supports --provider <name> --model <pattern>
	// - supports --provider <name> to select that provider's default model
	// - supports --model <provider>/<pattern>
	if (parsed.model || parsed.provider) {
		const resolved = parsed.model
			? resolveCliModel({
					cliProvider: parsed.provider,
					cliModel: parsed.model,
					modelRegistry,
				})
			: resolveCliProviderDefault({
					cliProvider: parsed.provider,
					modelRegistry,
				});
		if (resolved.warning) {
			diagnostics.push({ type: "warning", message: resolved.warning });
		}
		if (resolved.error) {
			diagnostics.push({ type: "error", message: resolved.error });
		}
		if (resolved.model) {
			options.model = resolved.model;
			// Allow "--model <pattern>:<thinking>" as a shorthand.
			// Explicit --thinking still takes precedence (applied later).
			if (parsed.model && !parsed.thinking && resolved.thinkingLevel) {
				options.thinkingLevel = resolved.thinkingLevel;
				cliThinkingFromModel = true;
			}
		}
	}

	// Record whether model/thinking came from an explicit CLI flag (before the profile fills them
	// in below), so AgentSession can re-apply profile model/thinking on reload without clobbering
	// an explicit --model/--thinking.
	options.isExplicitModel = !!parsed.model;
	options.isExplicitThinking = !!parsed.thinking || cliThinkingFromModel;

	// Model/Thinking from active profiles if not explicitly set via CLI flags
	const activeProfileNames = settingsManager.getActiveResourceProfileNames();
	if (activeProfileNames.length > 0) {
		const profileSettings = resolveProfileModelSettings({
			activeProfileNames,
			registry: settingsManager.getProfileRegistry(),
			modelRegistry,
			cwd,
		});
		if (profileSettings.warning) {
			diagnostics.push({ type: "warning", message: profileSettings.warning });
		}
		if (profileSettings.error) {
			diagnostics.push({
				type: "warning",
				message: `Active profile model resolution error: ${profileSettings.error}`,
			});
		}
		if (!options.model && profileSettings.model) {
			options.model = profileSettings.model;
		}
		if (!parsed.thinking && profileSettings.thinkingLevel) {
			options.thinkingLevel = profileSettings.thinkingLevel;
		}
	}

	if (!options.model && scopedModels.length > 0 && !hasExistingSession) {
		// Check if saved default is in scoped models - use it if so, otherwise first scoped model
		const savedProvider = settingsManager.getDefaultProvider();
		const savedModelId = settingsManager.getDefaultModel();
		const savedModel = savedProvider && savedModelId ? modelRegistry.find(savedProvider, savedModelId) : undefined;
		const savedInScope = savedModel ? scopedModels.find((sm) => modelsAreEqual(sm.model, savedModel)) : undefined;

		if (savedInScope) {
			options.model = savedInScope.model;
			// Use thinking level from scoped model config if explicitly set
			if (!parsed.thinking && savedInScope.thinkingLevel) {
				options.thinkingLevel = savedInScope.thinkingLevel;
			}
		} else {
			options.model = scopedModels[0].model;
			// Use thinking level from first scoped model if explicitly set
			if (!parsed.thinking && scopedModels[0].thinkingLevel) {
				options.thinkingLevel = scopedModels[0].thinkingLevel;
			}
		}
	}

	// Thinking level from CLI (takes precedence over scoped model thinking levels set above)
	if (parsed.thinking) {
		options.thinkingLevel = parsed.thinking;
	}
	options.serviceTier = parsed.serviceTier;

	// Scoped models for Ctrl+P cycling
	// Keep thinking level undefined when not explicitly set in the model pattern.
	// Undefined means "inherit current session thinking level" during cycling.
	if (scopedModels.length > 0) {
		options.scopedModels = scopedModels.map((sm) => ({
			model: sm.model,
			thinkingLevel: sm.thinkingLevel,
		}));
	}

	// API key from CLI - set in authStorage
	// (handled by caller before createAgentSession)

	// Tools
	if (parsed.noTools) {
		options.noTools = "all";
	} else if (parsed.noBuiltinTools) {
		options.noTools = "builtin";
	}
	if (parsed.tools) {
		options.tools = [...parsed.tools];
	}
	if (parsed.excludeTools) {
		options.excludeTools = [...parsed.excludeTools];
	}

	return { options, cliThinkingFromModel, diagnostics };
}

function resolveCliPaths(cwd: string, paths: string[] | undefined): string[] | undefined {
	return paths?.map((value) => (isLocalPath(value) ? resolvePath(value, cwd) : value));
}

async function showStartupSelector<T>(
	settingsManager: SettingsManager,
	title: string,
	options: Array<{ label: string; value: T }>,
): Promise<T | undefined> {
	const [{ ExtensionSelectorComponent }, { initTheme }] = await Promise.all([
		import("./modes/interactive/components/extension-selector.ts"),
		import("./modes/interactive/theme/theme.ts"),
	]);
	initTheme(settingsManager.getTheme());
	setKeybindings(KeybindingsManager.create());

	return new Promise((resolve) => {
		const ui = new TUI(new ProcessTerminal(), settingsManager.getShowHardwareCursor());
		ui.setClearOnShrink(settingsManager.getClearOnShrink());

		let settled = false;
		const finish = (result: T | undefined) => {
			if (settled) {
				return;
			}
			settled = true;
			ui.stop();
			resolve(result);
		};

		const selector = new ExtensionSelectorComponent(
			title,
			options.map((option) => option.label),
			(option) => finish(options.find((entry) => entry.label === option)?.value),
			() => finish(undefined),
			{ tui: ui },
		);
		ui.addChild(selector);
		ui.setFocus(selector);
		ui.start();
	});
}

async function promptForMissingSessionCwd(
	issue: SessionCwdIssue,
	settingsManager: SettingsManager,
): Promise<string | undefined> {
	return showStartupSelector(settingsManager, formatMissingSessionCwdPrompt(issue), [
		{ label: "Continue", value: issue.fallbackCwd },
		{ label: "Cancel", value: undefined },
	]);
}

interface ProjectTrustPromptResult {
	trusted: boolean;
	remember: boolean;
}

async function promptForProjectTrust(
	cwd: string,
	settingsManager: SettingsManager,
): Promise<ProjectTrustPromptResult | undefined> {
	return showStartupSelector(
		settingsManager,
		`Trust project folder?\n${cwd}\n\nThis allows pi to read project instructions (AGENTS.md/CLAUDE.md/GEMINI.md), load .pi settings and resources, install missing project packages, and execute project extensions.`,
		[
			{ label: "Trust", value: { trusted: true, remember: true } },
			{ label: "Trust (this session only)", value: { trusted: true, remember: false } },
			{ label: "Do not trust", value: { trusted: false, remember: true } },
			{ label: "Do not trust (this session only)", value: { trusted: false, remember: false } },
		],
	);
}

async function resolveProjectTrusted(options: {
	cwd: string;
	trustStore: ProjectTrustStore;
	trustOverride?: boolean;
	appMode: AppMode;
	settingsManagerForPrompt: SettingsManager;
}): Promise<boolean> {
	if (options.trustOverride !== undefined) {
		return options.trustOverride;
	}
	if (!hasProjectTrustInputs(options.cwd)) {
		return true;
	}

	const decision = options.trustStore.get(options.cwd);
	if (decision !== null) {
		return decision;
	}
	if (options.appMode !== "interactive") {
		return false;
	}

	const selected = await promptForProjectTrust(options.cwd, options.settingsManagerForPrompt);
	if (selected !== undefined) {
		if (selected.remember) {
			options.trustStore.set(options.cwd, selected.trusted);
		}
		return selected.trusted;
	}
	return false;
}

export interface MainOptions {
	extensionFactories?: ExtensionFactory[];
}

export async function main(args: string[], options?: MainOptions) {
	resetTimings();
	const workAgentDir = getAgentDir();
	process.env[PI_WORK_ROOT_ENV] = getWorkRoot(workAgentDir);
	if (process.env.PI_TUI_DEBUG === "1") {
		process.env.PI_TUI_WORK_DIR = getProcessWorkRun(workAgentDir, "debug", "tui").path;
	}
	const offlineMode = args.includes("--offline") || isTruthyEnvFlag(process.env.PI_OFFLINE);
	if (offlineMode) {
		process.env.PI_OFFLINE = "1";
		process.env.PI_SKIP_VERSION_CHECK = "1";
	}

	if (process.platform === "win32") {
		cleanupWindowsSelfUpdateQuarantine(getPackageDir());
	}

	if (args[0] === "doctor") {
		// Interactive command: show fff-node install progress rather than a
		// silent multi-second gap (runUpdatePreflight's background run stays
		// quiet -- see the `{ silent: true }` default on runDoctor).
		const report = await runDoctor(undefined, { silent: false });
		console.log(formatDoctorReport(report));
		process.exitCode = report.checks.some((check) => !check.present) ? 1 : 0;
		return;
	}

	if (await handlePackageCommand(args)) {
		// Verify + provision required tooling right after an update, instead of
		// leaving it to lazy first-use (see doctor.ts's module doc). Best-effort
		// and non-fatal: runUpdatePreflight() never throws, so a broken
		// environment check can't turn a successful update into a failed one.
		if (args[0] === "update" && process.exitCode !== 1) {
			await runUpdatePreflight();
		}
		return;
	}

	if (await handleConfigCommand(args)) {
		return;
	}

	const parsed = parseArgs(args);
	if (parsed.diagnostics.length > 0) {
		for (const d of parsed.diagnostics) {
			const color = d.type === "error" ? chalk.red : chalk.yellow;
			console.error(color(`${d.type === "error" ? "Error" : "Warning"}: ${d.message}`));
		}
		if (parsed.diagnostics.some((d) => d.type === "error")) {
			process.exit(1);
		}
	}
	time("parseArgs");
	// --worktree-lane is sugar over the cross-process env contract: setting the env here (before
	// any session/tool construction) makes the flag and a launcher-provided env byte-identical
	// downstream (RuntimeBuilder's lane gate, the epoch watcher, child processes).
	if (parsed.worktreeLane) {
		process.env[PI_WORKTREE_LANE_ENV] = parsed.worktreeLane;
	}
	// --parent-pid/--parent-session are the same CLI-sugar-over-env pattern as --worktree-lane:
	// setting the env here (before any session/tool construction) makes the flag and a
	// launcher-provided env byte-identical downstream (the process-matrix runtime's worker branch).
	if (parsed.parentPid !== undefined) {
		process.env[PI_PARENT_PID_ENV] = String(parsed.parentPid);
	}
	if (parsed.parentSession) {
		process.env[PI_PARENT_SESSION_ENV] = parsed.parentSession;
	}
	if (parsed.taskRef) {
		process.env[PI_TASK_REF_ENV] = parsed.taskRef;
	}
	if (parsed.sessionMode) {
		setTerminalSessionMode(parsed.sessionMode);
	}
	let appMode = resolveAppMode(parsed, process.stdin.isTTY);
	let hasHumanUI = appMode === "interactive" && getSessionRole() === "main";
	const shouldTakeOverStdout = appMode !== "interactive";
	if (shouldTakeOverStdout) {
		takeOverStdout();
	}

	if (parsed.version) {
		console.log(VERSION);
		process.exit(0);
	}

	if (parsed.export) {
		let result: string;
		try {
			const outputPath = parsed.messages.length > 0 ? parsed.messages[0] : undefined;
			result = await exportFromFile(parsed.export, outputPath);
		} catch (error: unknown) {
			const message = error instanceof Error ? error.message : "Failed to export session";
			console.error(chalk.red(`Error: ${message}`));
			process.exit(1);
		}
		console.log(`Exported to: ${result}`);
		process.exit(0);
	}

	if (parsed.mode === "rpc" && parsed.fileArgs.length > 0) {
		console.error(chalk.red("Error: @file arguments are not supported in RPC mode"));
		process.exit(1);
	}

	validateForkFlags(parsed);
	validateSessionIdFlags(parsed);

	// Run migrations (pass cwd for project-local migrations)
	const { migratedAuthProviders: migratedProviders, deprecationWarnings } = runMigrations(process.cwd());
	time("runMigrations");

	const cwd = process.cwd();
	const agentDir = getAgentDir();
	const trustStore = new ProjectTrustStore(agentDir);
	const startupProjectTrusted =
		parsed.projectTrustOverride ?? (!hasProjectTrustInputs(cwd) || trustStore.get(cwd) === true);
	const startupSettingsManager = SettingsManager.create(cwd, agentDir, { projectTrusted: startupProjectTrusted });
	reportDiagnostics(collectSettingsDiagnostics(startupSettingsManager, "startup session lookup"));

	// Decide the final runtime cwd before creating cwd-bound runtime services.
	// --session and --resume may select a session from another project, so project-local
	// settings, resources, provider registrations, and models must be resolved only after
	// the target session cwd is known. The startup-cwd settings manager is used only for
	// sessionDir lookup during session selection.
	const envSessionDir = process.env[ENV_SESSION_DIR];
	const sessionDir =
		(parsed.sessionDir ? normalizePath(parsed.sessionDir) : undefined) ??
		(envSessionDir ? expandTildePath(envSessionDir) : undefined) ??
		startupSettingsManager.getSessionDir();
	let sessionManager = await createSessionManager(parsed, cwd, sessionDir, startupSettingsManager, hasHumanUI);
	const missingSessionCwdIssue = getMissingSessionCwdIssue(sessionManager, cwd);
	if (missingSessionCwdIssue) {
		if (hasHumanUI) {
			const selectedCwd = await promptForMissingSessionCwd(missingSessionCwdIssue, startupSettingsManager);
			if (!selectedCwd) {
				process.exit(0);
			}
			sessionManager = openSession(missingSessionCwdIssue.sessionFile!, sessionDir, selectedCwd);
		} else {
			console.error(chalk.red(new MissingSessionCwdError(missingSessionCwdIssue).message));
			process.exit(1);
		}
	}
	if (parsed.name !== undefined) {
		const name = parsed.name.trim();
		if (!name) {
			console.error(chalk.red("Error: --name requires a non-empty value"));
			process.exit(1);
		}
		sessionManager.appendSessionInfo(name);
	}
	time("createSessionManager");

	const trustPromptMode: AppMode = parsed.help || parsed.listModels !== undefined || !hasHumanUI ? "print" : appMode;
	const projectTrustedForSession = await resolveProjectTrusted({
		cwd: sessionManager.getCwd(),
		trustStore,
		trustOverride: parsed.projectTrustOverride,
		appMode: trustPromptMode,
		settingsManagerForPrompt: startupSettingsManager,
	});

	const resolvedExtensionPaths = resolveCliPaths(cwd, parsed.extensions);
	const resolvedSkillPaths = resolveCliPaths(cwd, parsed.skills);
	const resolvedPromptTemplatePaths = resolveCliPaths(cwd, parsed.promptTemplates);
	const resolvedThemePaths = resolveCliPaths(cwd, parsed.themes);
	const authStorage = AuthStorage.create();
	const createRuntime: CreateAgentSessionRuntimeFactory = async ({
		cwd,
		agentDir,
		sessionManager,
		sessionStartEvent,
	}) => {
		const projectTrusted =
			cwd === sessionManager.getCwd()
				? projectTrustedForSession
				: (parsed.projectTrustOverride ?? (!hasProjectTrustInputs(cwd) || trustStore.get(cwd) === true));
		const runtimeSettingsManager = SettingsManager.create(cwd, agentDir, { projectTrusted });
		const orchestrationDiagnostics: AgentSessionRuntimeDiagnostic[] = [];
		const orchestrationProfileId =
			parsed.orchestrationProfile?.trim() || runtimeSettingsManager.getActiveOrchestrationProfile();
		let orchestrationProfile: OrchestrationProfile | undefined;
		if (orchestrationProfileId) {
			const loaded = new OrchestrationProfileStore({ agentDir, cwd, projectTrusted }).load();
			orchestrationDiagnostics.push(
				...loaded.diagnostics.map((diagnostic) => ({
					type: "warning" as const,
					message: `Orchestration profile ignored (${diagnostic.path}): ${diagnostic.message}`,
				})),
			);
			orchestrationProfile = loaded.registry.get(orchestrationProfileId);
			if (!orchestrationProfile) {
				orchestrationDiagnostics.push({
					type: "error",
					message: `Orchestration profile '${orchestrationProfileId}' was not found.`,
				});
			} else {
				runtimeSettingsManager.setRuntimeResourceProfiles([...orchestrationProfile.resourceProfileNames]);
				const conflictingFlags = [
					...(parsed.model || parsed.provider ? ["--model/--provider"] : []),
					...(parsed.models ? ["--models"] : []),
					...(parsed.thinking ? ["--thinking"] : []),
					...(parsed.tools || parsed.excludeTools || parsed.noTools || parsed.noBuiltinTools
						? ["--tools/--exclude-tools/--no-tools"]
						: []),
					...(parsed.resourceProfiles ? ["--resource-profile"] : []),
				];
				if (conflictingFlags.length > 0) {
					orchestrationDiagnostics.push({
						type: "error",
						message: `Orchestration profile '${orchestrationProfile.profileId}' owns model, thinking, tools, and resources; remove conflicting ${conflictingFlags.join(", ")}.`,
					});
				}
			}
		}
		if (parsed.resourceProfileJson) {
			for (const profileInput of parsed.resourceProfileJson) {
				const { profiles, errors } = parseResourceProfileInput(profileInput);
				if (errors.length > 0) {
					reportDiagnostics(
						errors.map((message) => ({
							type: "warning" as const,
							message: `--resource-profile-json ignored entry: ${message}`,
						})),
					);
				}
				runtimeSettingsManager.addInlineResourceProfileDefinitions(profiles);
			}
		}
		if (!orchestrationProfile && parsed.resourceProfiles && parsed.resourceProfiles.length > 0) {
			runtimeSettingsManager.setRuntimeResourceProfiles(parsed.resourceProfiles);
		}
		const services = await createAgentSessionServices({
			cwd,
			agentDir,
			authStorage,
			settingsManager: runtimeSettingsManager,
			extensionFlagValues: parsed.unknownFlags,
			resourceLoaderOptions: {
				additionalExtensionPaths: resolvedExtensionPaths,
				additionalSkillPaths: resolvedSkillPaths,
				additionalPromptTemplatePaths: resolvedPromptTemplatePaths,
				additionalThemePaths: resolvedThemePaths,
				noExtensions: parsed.noExtensions,
				noSkills: parsed.noSkills,
				noPromptTemplates: parsed.noPromptTemplates,
				noThemes: parsed.noThemes,
				noContextFiles: parsed.noContextFiles,
				systemPrompt: parsed.systemPrompt,
				appendSystemPrompt: parsed.appendSystemPrompt,
				extensionFactories: options?.extensionFactories,
			},
		});
		const { settingsManager, modelRegistry, resourceLoader } = services;
		const diagnostics: AgentSessionRuntimeDiagnostic[] = [
			...services.diagnostics,
			...orchestrationDiagnostics,
			...collectSettingsDiagnostics(settingsManager, "runtime creation"),
			...resourceLoader.getExtensions().errors.map(({ path, error }) => ({
				type: "warning" as const,
				message: `Failed to load extension "${path}": ${error}`,
			})),
		];

		diagnostics.push(
			...collectModelRouterConfigDiagnostics(settingsManager.getModelRouterSettings(), modelRegistry, agentDir).map(
				(message) => ({
					type: "warning" as const,
					message,
				}),
			),
		);

		const modelPatterns = parsed.models ?? settingsManager.getEnabledModels();
		const scopedModels =
			modelPatterns && modelPatterns.length > 0 ? await resolveModelScope(modelPatterns, modelRegistry) : [];
		const {
			options: sessionOptions,
			cliThinkingFromModel,
			diagnostics: sessionOptionDiagnostics,
		} = buildSessionOptions(
			parsed,
			scopedModels,
			sessionManager.buildSessionContext().messages.length > 0,
			modelRegistry,
			settingsManager,
			cwd,
		);
		diagnostics.push(...sessionOptionDiagnostics);
		let orchestrationModel: ReturnType<typeof resolveConfiguredOrchestrationModel>;
		if (orchestrationProfile) {
			orchestrationModel = resolveConfiguredOrchestrationModel(orchestrationProfile, modelRegistry);
			if (!orchestrationModel) {
				diagnostics.push({
					type: "error",
					message: `Orchestration profile '${orchestrationProfile.profileId}' has no configured, authenticated model that supports its exact thinking level.`,
				});
			} else {
				sessionOptions.model = orchestrationModel.model;
				sessionOptions.thinkingLevel = orchestrationModel.binding.thinkingLevel;
				sessionOptions.isExplicitModel = true;
				sessionOptions.isExplicitThinking = true;
				sessionOptions.scopedModels = [
					{ model: orchestrationModel.model, thinkingLevel: orchestrationModel.binding.thinkingLevel },
				];
				sessionOptions.tools = [...orchestrationProfile.toolNames];
				sessionOptions.excludeTools = undefined;
				sessionOptions.noTools = undefined;
				sessionOptions.orchestrationProfile = orchestrationProfile;
			}
		}

		if (parsed.apiKey) {
			if (!sessionOptions.model) {
				diagnostics.push({
					type: "error",
					message: "--api-key requires a model to be specified via --model, --provider/--model, or --models",
				});
			} else {
				authStorage.setRuntimeApiKey(sessionOptions.model.provider, parsed.apiKey);
			}
		}

		const created = await createAgentSessionFromServices({
			services,
			sessionManager,
			sessionStartEvent,
			model: sessionOptions.model,
			thinkingLevel: sessionOptions.thinkingLevel,
			serviceTier: sessionOptions.serviceTier,
			isExplicitModel: sessionOptions.isExplicitModel,
			isExplicitThinking: sessionOptions.isExplicitThinking,
			scopedModels: sessionOptions.scopedModels,
			tools: sessionOptions.tools,
			excludeTools: sessionOptions.excludeTools,
			noTools: sessionOptions.noTools,
			toolProfileFilter: settingsManager.getResourceProfileFilter("tools"),
			customTools: sessionOptions.customTools,
			orchestrationProfile: sessionOptions.orchestrationProfile,
		});
		const cliThinkingOverride = parsed.thinking !== undefined || cliThinkingFromModel;
		if (created.session.model && cliThinkingOverride) {
			created.session.setThinkingLevel(created.session.thinkingLevel);
		}
		if (orchestrationProfile && orchestrationModel) {
			const capabilities = [...orchestrationProfile.capabilityCeiling];
			created.session.capabilityEnvelope = {
				id: `orchestration-profile:${orchestrationProfile.profileId}`,
				profileId: orchestrationProfile.profileId,
				capabilities,
				allowedTools: [...orchestrationProfile.toolNames],
				...(capabilities.some(
					(capability) =>
						capability === "filesystem.read" ||
						capability === "filesystem.write" ||
						capability === "worktree.read" ||
						capability === "worktree.mutate",
				)
					? { allowedPaths: [cwd] }
					: {}),
				...(orchestrationProfile.budget.maxCostUsd !== undefined
					? { maxEstimatedUsd: orchestrationProfile.budget.maxCostUsd }
					: {}),
				createdAt: new Date().toISOString(),
			};
		}

		return {
			...created,
			services,
			diagnostics,
		};
	};
	time("createRuntime");
	const runtime = await createAgentSessionRuntime(createRuntime, {
		cwd: sessionManager.getCwd(),
		agentDir,
		sessionManager,
	});
	time("createAgentSessionRuntime");
	const { services, session, modelFallbackMessage } = runtime;
	const { settingsManager, modelRegistry, resourceLoader } = services;
	configureHttpDispatcher(settingsManager.getHttpIdleTimeoutMs());
	if (parsed.resume || parsed.continue || parsed.session !== undefined) {
		// Restoring a session must not mutate owner intent. Active goals may resume their runtime;
		// paused/blocked/limited goals remain stopped until an explicit owner action.
		session.restoreGoalRuntimeAfterResume();
	}

	// A lane-bound session refuses AUTHORITATIVELY at its own startup when its model cannot reliably
	// drive the lane-gate/recovery surface unattended (full capability class, a declared context
	// window, an advertised native tool-call path, no graded /toolprobe demotion -- see
	// model-capability.ts's evaluateLaneWorkerRefusal). This is the authoritative check regardless of
	// how the lane binding arrived (--worktree-lane, PI_WORKTREE_LANE, or a launcher-set env): the
	// goal->tmux dispatch's own pre-check (tools/tmux-dispatch.ts) is the parent's best-effort guess
	// only and can race a model swap between dispatch and child startup. No silent unbinding -- an
	// ineligible model exits before the lane gate or epoch watcher ever start.
	const boundWorktreeLaneKey = getBoundWorktreeLaneKey();
	if (boundWorktreeLaneKey) {
		const laneWorkerRefusal = session.getLaneWorkerRefusal();
		if (laneWorkerRefusal) {
			console.error(chalk.red(formatLaneWorkerRefusal(laneWorkerRefusal, boundWorktreeLaneKey)));
			await disposeRuntimeAndExit(runtime, 1);
			return;
		}
	}

	// A read-only command (--help, --list-models) uses an in-memory session and has nothing to
	// supervise. Do not start either runtime: even an awaited startup would create needless state
	// and violate the read-only zero-write contract.
	const isReadOnlyCommand = parsed.help || parsed.listModels !== undefined;

	// Session-owned supervision is replaced with /new, /resume, and /fork. This keeps notices,
	// process identity, orphan recovery, and worktree watchers bound to the active session instead
	// of retaining the initial session after it has been disposed.
	if (!isReadOnlyCommand) {
		const supervision = new SessionSupervisionRuntime({
			agentDir,
			hasUI: hasHumanUI,
			orchestrationProfileId: parsed.orchestrationProfile?.trim() || undefined,
			isProcessAlive: isReloadSessionProcessAlive,
			promptConfirm,
			resumeWorker: launchResumableWorker,
			onDiagnostic: (message) => {
				console.error(chalk.yellow(`Warning: ${message}`));
			},
			requestExit: () => disposeRuntimeAndExit(runtime, 0),
		});
		runtime.registerSessionResource(supervision);
		await supervision.start(session);
	}

	if (parsed.help) {
		const extensionFlags = resourceLoader
			.getExtensions()
			.extensions.flatMap((extension) => Array.from(extension.flags.values()));
		printHelp(extensionFlags);
		await disposeRuntimeAndExit(runtime, 0);
		return;
	}

	if (parsed.listModels !== undefined) {
		const searchPattern = typeof parsed.listModels === "string" ? parsed.listModels : undefined;
		await listModels(modelRegistry, searchPattern);
		await disposeRuntimeAndExit(runtime, 0);
		return;
	}

	// Read piped stdin content (if any) - skip for RPC mode which uses stdin for JSON-RPC
	let stdinContent: string | undefined;
	if (appMode !== "rpc") {
		stdinContent = await readPipedInput(process.stdin, process.stdin.isTTY);
		if (stdinContent !== undefined && appMode === "interactive") {
			appMode = "print";
			hasHumanUI = false;
		}
	}
	time("readPipedStdin");

	const { initialMessage, initialImages } = await prepareInitialMessage(
		parsed,
		settingsManager.getImageAutoResize(),
		stdinContent,
	);
	time("prepareInitialMessage");
	const { initTheme, stopThemeWatcher } = await import("./modes/interactive/theme/theme.ts");
	initTheme(settingsManager.getTheme(), hasHumanUI);
	time("initTheme");

	// Show deprecation warnings in interactive mode
	if (hasHumanUI && deprecationWarnings.length > 0) {
		await showDeprecationWarnings(deprecationWarnings);
	}

	time("resolveModelScope");
	reportDiagnostics(runtime.diagnostics);
	if (runtime.diagnostics.some((diagnostic) => diagnostic.type === "error")) {
		await disposeRuntimeAndExit(runtime, 1);
		return;
	}
	time("createAgentSession");

	if (appMode !== "interactive" && !session.model) {
		console.error(chalk.red(formatNoModelsAvailableMessage()));
		await disposeRuntimeAndExit(runtime, 1);
		return;
	}

	const startupBenchmark = isTruthyEnvFlag(process.env.PI_STARTUP_BENCHMARK);
	if (startupBenchmark && appMode !== "interactive") {
		console.error(chalk.red("Error: PI_STARTUP_BENCHMARK only supports interactive mode"));
		await disposeRuntimeAndExit(runtime, 1);
		return;
	}

	if (appMode === "rpc") {
		printTimings();
		const { runRpcMode } = await import("./modes/rpc/rpc-mode.ts");
		await runRpcMode(runtime);
	} else if (appMode === "interactive") {
		const { InteractiveMode } = await import("./modes/interactive/interactive-mode.ts");
		const interactiveMode = new InteractiveMode(runtime, {
			migratedProviders,
			modelFallbackMessage,
			initialMessage,
			initialImages,
			initialMessages: parsed.messages,
			verbose: parsed.verbose,
			hasHumanAudience: hasHumanUI,
		});
		if (startupBenchmark) {
			await interactiveMode.init();
			time("interactiveMode.init");
			printTimings();
			interactiveMode.stop();
			stopThemeWatcher();
			if (process.stdout.writableLength > 0) {
				await new Promise<void>((resolve) => process.stdout.once("drain", resolve));
			}
			if (process.stderr.writableLength > 0) {
				await new Promise<void>((resolve) => process.stderr.once("drain", resolve));
			}
			await disposeRuntimeAndExit(runtime, 0);
			return;
		}

		printTimings();
		await interactiveMode.run();
	} else {
		printTimings();
		const { runPrintMode } = await import("./modes/print-mode.ts");
		const exitCode = await runPrintMode(runtime, {
			mode: toPrintOutputMode(appMode),
			messages: parsed.messages,
			initialMessage,
			initialImages,
			printUsage: parsed.printUsage,
		});
		stopThemeWatcher();
		restoreStdout();
		if (exitCode !== 0) {
			process.exitCode = exitCode;
		}
		return;
	}
}
